"use client";

import type React from "react";
import { MessageCircle, Lock, User, Sparkles } from "lucide-react";
import Link from "next/link";

import { useAuth } from "@/context/auth-context";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface LoginRequiredProps {
  title?: string;
  description?: string;
  children?: React.ReactNode;
}

export function LoginRequired({
  title = "Login Required",
  description = "Please log in to access this feature",
  children,
}: LoginRequiredProps): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription className="text-center">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <MessageCircle className="h-4 w-4 text-primary" />
              <span>Get personalized AI tutoring assistance</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>Save your chat history and progress</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <User className="h-4 w-4 text-primary" />
              <span>Access exclusive member features</span>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <Button asChild className="w-full">
              <Link href="/login">Login</Link>
            </Button>
            <Button variant="outline" asChild className="w-full">
              <Link href="/register">Create Account</Link>
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Join thousands of students getting AI-powered exam help
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
